/**
 * Prospect Media Service
 * Handles API requests for prospect videos and documents
 */

import { get, post, put, del } from './api';
import type {
  CreateVideoClipsRequest,
  CreateVideoClipsResponse,
  ProspectVideo,
  SetVideoTagsResponse,
  VideoDownloadUrlResponse,
  VideoUploadUrlRequest,
  VideoUploadUrlResponse,
} from '../types/prospect-media';

/**
 * Request presigned upload URLs for video and thumbnail
 */
export const requestVideoUploadUrl = async (
  prospectId: string,
  request: VideoUploadUrlRequest
): Promise<VideoUploadUrlResponse> => {
  return post<VideoUploadUrlResponse>(
    `/api/prospects/${prospectId}/ProspectMedia/videos/upload-url`,
    request
  );
};

/**
 * Upload video file to GCS using presigned URL
 * Uses XMLHttpRequest for progress tracking (fetch API doesn't support upload progress)
 */
export const uploadVideoToGCS = async (
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: (percent: number) => void
): Promise<{ success: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();

    // Set 5-minute timeout
    xhr.timeout = 300000; // 5 minutes in milliseconds

    // Timeout handler
    xhr.ontimeout = () => {
      const errorMsg =
        'Upload timed out after 5 minutes. Please check your connection and try again.';
      console.error('[ProspectMedia] Upload timeout', {
        fileName: file.name,
        fileSize: file.size,
        fileSizeMB: (file.size / (1024 * 1024)).toFixed(1),
      });
      resolve({ success: false, error: errorMsg });
    };

    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 204) {
        resolve({ success: true });
      } else {
        const errorMsg = `GCS upload failed: ${xhr.status} ${xhr.statusText}`;
        console.error('[ProspectMedia]', errorMsg, {
          status: xhr.status,
          statusText: xhr.statusText,
          fileName: file.name,
          fileSize: file.size,
        });
        resolve({ success: false, error: errorMsg });
      }
    };

    xhr.onerror = (event) => {
      const errorMsg = 'Network error during video upload';
      console.error('[ProspectMedia]', errorMsg, {
        event,
        fileName: file.name,
        fileSize: file.size,
      });
      resolve({ success: false, error: errorMsg });
    };

    xhr.onabort = () => {
      const errorMsg = 'Upload was cancelled';
      console.error('[ProspectMedia] Video upload cancelled', {
        fileName: file.name,
        fileSize: file.size,
      });
      resolve({ success: false, error: errorMsg });
    };

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
};

/**
 * Upload thumbnail to GCS using presigned URL
 */
export const uploadThumbnailToGCS = async (
  uploadUrl: string,
  thumbnailBlob: Blob
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/jpeg' },
      body: thumbnailBlob,
      // 30-second timeout for thumbnail upload
      signal: AbortSignal.timeout(30000),
    });

    if (!response.ok) {
      const errorMsg = `Thumbnail upload failed: ${response.status} ${response.statusText}`;
      console.error('[ProspectMedia]', errorMsg, {
        status: response.status,
        statusText: response.statusText,
        thumbnailSize: thumbnailBlob.size,
      });
      return { success: false, error: errorMsg };
    }

    return { success: true };
  } catch (error) {
    // Handle timeout-specific error
    if (error instanceof Error && error.name === 'TimeoutError') {
      console.error('[ProspectMedia] Thumbnail upload timeout', {
        thumbnailSize: thumbnailBlob.size,
      });
      return {
        success: false,
        error: 'Thumbnail upload timed out. Please try again.',
      };
    }

    console.error('[ProspectMedia] Thumbnail upload failed', {
      error,
      thumbnailSize: thumbnailBlob.size,
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Fetch a prospect's videos, optionally narrowed.
 *
 * Filtering server-side rather than in the component keeps one definition of what a
 * search matches — the gallery and the org-wide list would otherwise drift on whether
 * a tag name counts as a hit.
 */
export const getVideos = async (
  prospectId: string,
  filters?: Partial<VideoFilters>
): Promise<ProspectVideo[]> => {
  const query = buildVideoFilterQuery(filters);
  const suffix = query ? `?${query}` : '';
  return get<ProspectVideo[]>(`/api/prospects/${prospectId}/ProspectMedia/videos${suffix}`);
};

/**
 * A short-lived link that downloads a video rather than playing it. For a clip the link is
 * the full source video (`isFullSourceVideo`), since clips have no file of their own.
 */
export const getVideoDownloadUrl = async (
  prospectId: string,
  videoId: string
): Promise<VideoDownloadUrlResponse> => {
  return get<VideoDownloadUrlResponse>(
    `/api/prospects/${prospectId}/ProspectMedia/videos/${videoId}/download-url`
  );
};

/**
 * Delete a video (soft delete)
 */
export const deleteVideo = async (prospectId: string, videoId: string): Promise<void> => {
  return del<void>(`/api/prospects/${prospectId}/ProspectMedia/videos/${videoId}`);
};

/**
 * Cut a source video into one or more clips. Each clip becomes its own video record
 * pointing at the source's file — nothing is re-encoded or re-uploaded.
 *
 * Returns a signed thumbnail upload URL per clip so the caller can push a poster frame
 * captured at the clip's start.
 */
export const createVideoClips = async (
  prospectId: string,
  videoId: string,
  request: CreateVideoClipsRequest
): Promise<CreateVideoClipsResponse> => {
  return post<CreateVideoClipsResponse>(
    `/api/prospects/${prospectId}/ProspectMedia/videos/${videoId}/clips`,
    request
  );
};

/**
 * Replace a video's labels with exactly `tagIds`. An empty array clears them.
 *
 * Set semantics rather than add/remove — the picker already knows the full selection, so
 * a replace can't drift out of sync the way a stream of toggles can.
 */
export const setVideoTags = async (
  prospectId: string,
  videoId: string,
  tagIds: string[]
): Promise<SetVideoTagsResponse> => {
  return put<SetVideoTagsResponse>(
    `/api/prospects/${prospectId}/ProspectMedia/videos/${videoId}/tags`,
    { tagIds }
  );
};

/**
 * Get all videos for the organization with prospect information
 * Supports pagination and filtering
 */
import type { PaginatedResponse } from '../types/api';
import type {
  VideoFilterOptions,
  VideoFilters,
  VideoWithProspect,
} from '../types/prospect-media';

export type GetAllVideosParams = Partial<VideoFilters> & {
  pageNumber?: number;
  pageSize?: number;
  prospectId?: string;
};

/**
 * Serializes the filter axes both video endpoints share. Blank strings and nulls are
 * omitted entirely — sending `teamId=` would bind to Guid.Empty server-side.
 */
const buildVideoFilterQuery = (
  filters: (Partial<VideoFilters> & { prospectId?: string }) | undefined,
  queryParams: URLSearchParams = new URLSearchParams()
): string => {
  if (!filters) return queryParams.toString();

  const searchTerm = filters.searchTerm?.trim();
  if (searchTerm) {
    queryParams.append('searchTerm', searchTerm);
  }

  // Repeated key rather than a joined list — matches ASP.NET's Guid[] binding.
  filters.tagIds?.forEach((tagId) => queryParams.append('tagIds', tagId));

  if (filters.teamId) queryParams.append('teamId', filters.teamId);
  if (filters.prospectId) queryParams.append('prospectId', filters.prospectId);
  if (filters.uploadedBy) queryParams.append('uploadedBy', filters.uploadedBy);

  // Sent as bare dates. The API reads a date-only upper bound as through the end of
  // that day, so picking one day at both ends returns that day's footage.
  if (filters.uploadedAfter) queryParams.append('uploadedAfter', filters.uploadedAfter);
  if (filters.uploadedBefore) queryParams.append('uploadedBefore', filters.uploadedBefore);

  return queryParams.toString();
};

export const getAllVideos = async (
  params: GetAllVideosParams = {}
): Promise<PaginatedResponse<VideoWithProspect>> => {
  const { pageNumber = 1, pageSize = 15, ...filters } = params;

  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });

  return get<PaginatedResponse<VideoWithProspect>>(
    `/api/videos?${buildVideoFilterQuery(filters, queryParams)}`
  );
};

/**
 * The choices the filter pickers render: the tag vocabulary, who has uploaded, and which
 * teams have footage. Fetched once per session rather than per filter change — options
 * that shrink as you narrow leave no way back to a wider result.
 */
export const getVideoFilterOptions = async (): Promise<VideoFilterOptions> => {
  return get<VideoFilterOptions>('/api/videos/filter-options');
};

// ============================================================================
// Document Methods
// ============================================================================

import type {
  ProspectDocument,
  DocumentUploadUrlRequest,
  DocumentUploadUrlResponse,
} from '../types/prospect-media';

/**
 * Request presigned upload URL for document
 */
export const requestDocumentUploadUrl = async (
  prospectId: string,
  request: DocumentUploadUrlRequest
): Promise<DocumentUploadUrlResponse> => {
  return post<DocumentUploadUrlResponse>(
    `/api/prospects/${prospectId}/ProspectMedia/documents/upload-url`,
    request
  );
};

/**
 * Upload document file to GCS using presigned URL
 * Uses XMLHttpRequest for progress tracking (fetch API doesn't support upload progress)
 */
export const uploadDocumentToGCS = async (
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: (percent: number) => void
): Promise<{ success: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();

    // Set 5-minute timeout
    xhr.timeout = 300000; // 5 minutes in milliseconds

    // Timeout handler
    xhr.ontimeout = () => {
      const errorMsg =
        'Upload timed out after 5 minutes. Please check your connection and try again.';
      console.error('[ProspectMedia] Document upload timeout', {
        fileName: file.name,
        fileSize: file.size,
        fileSizeMB: (file.size / (1024 * 1024)).toFixed(1),
      });
      resolve({ success: false, error: errorMsg });
    };

    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 204) {
        resolve({ success: true });
      } else {
        const errorMsg = `GCS upload failed: ${xhr.status} ${xhr.statusText}`;
        console.error('[ProspectMedia]', errorMsg, {
          status: xhr.status,
          statusText: xhr.statusText,
          fileName: file.name,
          fileSize: file.size,
        });
        resolve({ success: false, error: errorMsg });
      }
    };

    xhr.onerror = (event) => {
      const errorMsg = 'Network error during document upload';
      console.error('[ProspectMedia]', errorMsg, {
        event,
        fileName: file.name,
        fileSize: file.size,
      });
      resolve({ success: false, error: errorMsg });
    };

    xhr.onabort = () => {
      const errorMsg = 'Upload was cancelled';
      console.error('[ProspectMedia] Document upload cancelled', {
        fileName: file.name,
        fileSize: file.size,
      });
      resolve({ success: false, error: errorMsg });
    };

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
};

/**
 * Fetch all documents for a prospect
 */
export const getDocuments = async (prospectId: string): Promise<ProspectDocument[]> => {
  return get<ProspectDocument[]>(`/api/prospects/${prospectId}/ProspectMedia/documents`);
};

/**
 * Delete a document (soft delete)
 */
export const deleteDocument = async (prospectId: string, documentId: string): Promise<void> => {
  return del<void>(`/api/prospects/${prospectId}/ProspectMedia/documents/${documentId}`);
};
